import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import { Form, Button } from "reactstrap";
import cx from "classnames";
import { v4 as uuid4 } from "uuid";

import {
  DOCUMENT_PUBLISH_TO_TEMPLATE_LIBRARY_SUCCESS,
  DOCUMENT_PUBLISH_TO_TEMPLATE_LIBRARY_FAILED,
  DOCUMENT_PUBLISH_TO_TEMPLATE_LIBRARY_INFO_SUCCESS,
  DOCUMENT_PUBLISH_TO_TEMPLATE_LIBRARY_INFO_FAILED,
} from "../../../store/actions/actionTypes";
import {
  publishDocumentToTemplateLibrary,
  publishDocumentToTemplateLibraryInfo,
} from "../../../store/actions/documentActions";
import { fetchTemplateCategoriesByType } from "../../../store/actions/templateActions";
import { generateSelectedPagesArray, preventFormSubmitOnEnter as preventSubmit } from "../../../_helpers/utils";
import { Input } from "../../ui/input";
import { Icon } from "../../ui/icon";
import { ProcessLoader } from "../../ui/loader/processLoader";
import { FlashAlert } from "../../ui/flashAlert";

import global from "../../../scss/dhp.scss";
import { useSocket } from "../../../hooks/useSocket";
import { useLocalStorage } from "../../../hooks/useStorage";
import {
  PUBLISH_MERGE_TEMPLATE_STREAMING_START,
  PUBLISH_MERGE_TEMPLATE_STREAMING_SUCCESS,
  PUBLISH_TEMPLATE_STREAMING_START,
  STREAMING_COMPLETE,
} from "../../../constants/socket";
import { TEMPLATE_GROUP_VIDEO } from "../../../constants/pageSizeModal";
import ProgressLoader from "../../../assets/images/dh-loader.svg";

let style = Object.assign({}, global);

const PublishToTemplateLibraryModal = ({ document: data, _helpers }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const webSocket = useSocket();
  const eventIdRef = useRef(uuid4());
  const [templateSizes] = useLocalStorage("templateSizes");
  const alertData = {
    show: false,
    type: "",
  };
  const errorTypes = [DOCUMENT_PUBLISH_TO_TEMPLATE_LIBRARY_FAILED, DOCUMENT_PUBLISH_TO_TEMPLATE_LIBRARY_INFO_FAILED];
  const processEndTypes = [DOCUMENT_PUBLISH_TO_TEMPLATE_LIBRARY_SUCCESS, DOCUMENT_PUBLISH_TO_TEMPLATE_LIBRARY_FAILED];
  const modalName = data.published_library_template ? "Republish to Template Library" : "Publish to Template Library";
  const ButtonName = data.published_library_template ? "Republish" : "Publish";

  const form = {
    group: {
      value: data?.template_info?.group_id,
      valid: true,
    },
    type: {
      value: "",
      options: [],
      validation: {
        required: true,
      },
      valid: false,
    },
    category: {
      value: "",
      options: [],
      validation: {
        required: true,
      },
      valid: false,
    },
    tags: {
      value: [],
      valid: true,
    },
    premium: {
      value: false,
      valid: true,
    },
  };

  const [formData, setFormData] = useState(form);
  const [formValid, setFormValid] = useState(false);
  const [defaultType, setDefaultType] = useState();
  const [defaultCategory, setDefaultCategory] = useState();
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [alert, setAlert] = useState(alertData);
  const [disable, setDisable] = useState({ publish: false });
  const [showProcessLoader, setShowProcessLoader] = useState(false);

  const { type, template, id: templateId } = useSelector(state => state?.document);
  const { companyId, userId, region } = useSelector(state => {
    const auth = state?.auth || {};
    return {
      companyId: auth?.user?.company?.id || null,
      userId: auth?.uid || null,
      region: state?.auth?.company?.cluster_code ? state.auth.company.cluster_code.toLowerCase() : null,
    };
  });
  const documentData = useSelector(state => state?.document?.documentDetails?.data);

  const toggleAlert = () => setAlert({ ...alert, show: !alert.show });

  const generateDependencyOptions = field => {
    let updatedForm = { ...formData };
    if (field === "type") {
      updatedForm[field].options = _helpers.gridView.templates.groupObj[data?.template_info?.group_id]?.type;
    }
    if (field === "category") {
      updatedForm[field].options = _helpers.gridView.templates.typeObj[updatedForm.type.value]?.category;

      // upgrade: show all template categories (published + unpublished) by type for manager superadmin
      const type = _helpers.gridView.templates.typeObj[updatedForm.type.value];
      dispatch(fetchTemplateCategoriesByType(type.slug, true)).then(resp => {
        updatedForm[field].options = resp?.result?.template_categories;
        setFormData(updatedForm);
      });
    }
    setFormData(updatedForm);
  };

  const checkValidity = (value, rules) => {
    let isValid = true;
    if (rules.required) isValid = value.trim() !== "" && isValid;
    return isValid;
  };

  const updateForm = (e, field) => {
    let updatedForm = { ...formData };
    if (["type", "category"].includes(field)) {
      updatedForm[field].value = e.id;
      updatedForm[field].valid = checkValidity(updatedForm[field].value, updatedForm[field].validation);
      if (field === "type" && updatedForm.type.valid) {
        generateDependencyOptions("category");
        if (updatedForm.category.valid) {
          setDefaultCategory(null);
          updatedForm.category.value = "";
          updatedForm.category.valid = false;
        }
      }
      if (field === "category" && updatedForm.category.valid) {
        setDefaultCategory(e);
      }
    }
    if (field === "tags") {
      updatedForm[field].value = e;
    }
    if (field === "premium") {
      updatedForm[field].value = e?.target?.checked ?? e;
    }
    setFormData(updatedForm);
    setFormValid(!Object.keys(formData).some(field => !formData[field].valid));
  };

  const handleSubmit = e => {
    if (formValid) {
      e.preventDefault();
      setIsMounted(true);
      let payload = {
        documentId: data.id,
        group: formData.group.value,
        type: formData.type.value,
        category: formData.category.value,
        tags: formData.tags.value,
        premium: formData.premium.value,
        enabled: true,
      };
      dispatch(publishDocumentToTemplateLibrary(payload));
      setDisable({ publish: true });
    }
  };

  const generateVideoTemplateThumb = () => {
    let templateTypeId = formData.type.value;
    let { thumb_height, thumb_width } = templateSizes[templateTypeId];
    const length = documentData?.version?.rawdata?.contexts?.blockNodes?.length ?? 0;
    const subscribe = {
      type: PUBLISH_TEMPLATE_STREAMING_START,
      eventId: eventIdRef.current,
      region,
      companyId,
      userId,
      entryId: data.id,
      favourites: generateSelectedPagesArray(Math.max(length, 1)),
      templateId,
      thumbDimension: {
        height: thumb_height,
        width: thumb_width,
      },
      create: "template",
      actionType: "media",
      ref: `${window.location.origin}/editor/${data.id}`,
      fileName: documentData?.document_name,
    };
    if (webSocket?.readyState === 1) {
      webSocket.send(JSON.stringify(subscribe));
    }
  };

  const generateMergeTemplateThumb = () => {
    const subscribe = {
      type: PUBLISH_MERGE_TEMPLATE_STREAMING_START,
      entryId: templateId,
      eventId: eventIdRef.current,
      region: "Global",
      companyId,
      templateType: "dochipo",
    };
    if (webSocket?.readyState === 1) {
      webSocket.send(JSON.stringify(subscribe));
    }
  };

  const postApiCallActions = () => {
    if (type === DOCUMENT_PUBLISH_TO_TEMPLATE_LIBRARY_SUCCESS && templateId) {
      setIsMounted(false);
      if (_helpers.gridView.templates.groupObj[data?.template_info?.group_id]?.name === TEMPLATE_GROUP_VIDEO) {
        setShowProcessLoader(true);
        setAlert({
          ...alert,
          show: true,
          type: "custom",
          message: "Your video document is being processed",
          fadeIn: 20000000,
        });
        generateVideoTemplateThumb(); // generate mp4 thumb for video type documents
      } else if (
        (window.location.href.indexOf("/editor/") >= 0 && document.querySelectorAll(".merge-field").length > 0) ||
        (window.location.href.indexOf("/editor/") < 0 &&
          Object.values(data?.merge_fields ?? []).some(value => value === true))
      ) {
        setShowProcessLoader(true);
        generateMergeTemplateThumb();
      } else {
        setAlert({ ...alert, show: true, type: "success", message: "Document has been published to library" });
        setPublishSuccess(true);
      }
    }
    if (type === DOCUMENT_PUBLISH_TO_TEMPLATE_LIBRARY_INFO_SUCCESS) {
      setIsMounted(false);
      if (template?.template_type) {
        updateForm({ name: template?.type, id: template?.template_type }, "type");
        setDefaultType({ name: template?.type, id: template?.template_type });
      }
      if (template?.template_category) {
        updateForm({ name: template?.category, id: template?.template_category }, "category");
      }
      if (template?.tags) updateForm(template?.tags, "tags");
      if (template?.premium) {
        updateForm(template?.premium, "premium");
        document.getElementById("premium-toggle").checked = template?.premium;
      }
    }
    if (errorTypes.includes(type)) {
      setIsMounted(false);
      setAlert({ ...alert, show: true, type: "error", message: "Something went wrong!" });
    }
  };

  useEffect(() => {
    if (isMounted && type) postApiCallActions();
  }, [type, isMounted]);

  useEffect(() => {
    if (publishSuccess && !alert.show) {
      setPublishSuccess(false);
      _helpers.modal.toggle();

      if (_helpers.ref === "documents") {
        _helpers.gridView.fetch.setPayload({ ..._helpers.gridView.fetch.payload, page: 1 });
      }
      if (_helpers.ref === "editor") {
        history.go(0);
      }
    }
  }, [publishSuccess, alert.show]);

  useEffect(() => {
    if (_helpers.gridView.templates.groupObj) generateDependencyOptions("type");
  }, [_helpers.gridView.templates.groupObj]);

  useEffect(() => {
    if (data.published_library_template) {
      setIsMounted(true);
      dispatch(publishDocumentToTemplateLibraryInfo(data.id));
    }
  }, []);

  // socket receiver
  const onMessageGenerateThumb = useCallback(message => {
    const response = JSON.parse(message?.data);
    if (response.eventId !== eventIdRef.current) return;
    if (!response.success && response.type === STREAMING_COMPLETE) {
      setShowProcessLoader(false);
      setAlert({ ...alert, show: false, fadeIn: 0 });
      setTimeout(() => {
        setAlert({ ...alert, show: true, type: "error", message: "Something went wrong!" });
      }, 100);
    }

    if (response.success && response.type === STREAMING_COMPLETE) {
      setShowProcessLoader(false);
      setAlert({ ...alert, show: false, fadeIn: 0 });
      setTimeout(() => {
        setAlert({ ...alert, show: true, type: "success", message: "Document has been published to library" });
        setPublishSuccess(true);
      }, 100);
    }

    if (response.templateType === "dochipo" && response.type === PUBLISH_MERGE_TEMPLATE_STREAMING_SUCCESS) {
      setShowProcessLoader(false);
      setTimeout(() => {
        setAlert({ ...alert, show: true, type: "success", message: "Document has been published to library" });
        setPublishSuccess(true);
      }, 100);
    }
  }, []);

  useEffect(() => {
    if (webSocket?.readyState === 1) webSocket.addEventListener("message", onMessageGenerateThumb);

    return () => {
      webSocket?.removeEventListener("message", onMessageGenerateThumb);
    };
  }, [webSocket?.readyState]);

  return (
    <React.Fragment>
      <h4 className={cx(style["fw-7"], style["mb-4"])}>{modalName}</h4>
      <Link to="#" className={cx(style["cross-modal"], style["rounded"])} onClick={_helpers?.modal?.toggle}>
        <Icon icon="ui-close" />
      </Link>

      <Form onKeyDown={e => preventSubmit(e)} onSubmit={e => handleSubmit(e)}>
        <Input
          cssModule={style}
          returnType="formGroup"
          type="text"
          label="Document Group"
          readOnly={true}
          value={_helpers.gridView.templates.groupObj[data?.template_info?.group_id]?.name}
        />

        <Input
          cssModule={style}
          returnType="formGroup"
          type="dropdown"
          label="Document Type"
          required={true}
          classNamePrefix="select"
          getOptionValue={option => option?.name}
          getOptionLabel={option => <span>{option?.name}</span>}
          options={formData.type.options}
          updateState={e => updateForm(e, "type")}
          defaultValue={defaultType}
        />

        <Input
          cssModule={style}
          returnType="formGroup"
          type="dropdown"
          label="Document Category"
          required={true}
          classNamePrefix="select"
          getOptionValue={option => option?.name}
          getOptionLabel={option => <span>{option?.name}</span>}
          options={formData.category.options}
          updateState={e => updateForm(e, "category")}
          defaultValue={defaultCategory}
        />

        <Input
          returnType="formGroup"
          type="tag"
          label="Keywords"
          defaultValue={formData.tags.value}
          updateState={e => updateForm(e, "tags")}
        />

        <div className={cx(style["form-check"], style["form-switch"], style["lr-label"], style["mb-4"])}>
          <span className={style["left"]}>Free</span>
          <Input
            cssModule={style}
            returnType="noGroup"
            type="checkbox"
            className={style["form-check-input"]}
            id="premium-toggle"
            label="Premium"
            defaultChecked={formData.premium.value}
            onChange={e => updateForm(e, "premium")}
          />
        </div>

        <div className={cx(style["align-items-center"], style["d-flex"])}>
          <Button type="submit" color="primary" cssModule={style} disabled={!formValid || disable.publish}>
            {ButtonName}
          </Button>

          <ProcessLoader
            isOpen={
              disable.publish &&
              (!processEndTypes.includes(type) || (processEndTypes.includes(type) && showProcessLoader))
            }
            imgSrc={
              _helpers.gridView.templates.groupObj[data?.template_info?.group_id]?.name === TEMPLATE_GROUP_VIDEO
                ? ProgressLoader
                : false
            }
            imgWidth={
              _helpers.gridView.templates.groupObj[data?.template_info?.group_id]?.name === TEMPLATE_GROUP_VIDEO
                ? 24
                : false
            }
            imgClass="me-1"
          />

          <FlashAlert
            isOpen={alert.show}
            toggle={toggleAlert}
            type={alert.type}
            wrapperClass="ms-2"
            message={alert.message}
            fadeIn={alert?.fadeIn}
          />
        </div>
      </Form>
    </React.Fragment>
  );
};

PublishToTemplateLibraryModal.propTypes = {
  document: PropTypes.object.isRequired,
  _helpers: PropTypes.object.isRequired,
};

export default PublishToTemplateLibraryModal;
